import React, { useEffect, useState } from 'react';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Alert,
  useTheme, 
  useMediaQuery,
  Card,
  CardContent
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import axiosInstance from 'utils/axiosInstance';
import apiConfig from 'config/appConfig';
import moment from 'moment';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ResponseTimeChart = () => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(apiConfig.endpoints.requestLogs.paginated);
        const logs = response.data.requestLogs;

        // Process logs for response time analysis
        const timeData = processResponseTimeData(logs);
        
        if (!timeData.labels.length) {
          throw new Error("No response time data available.");
        }

        setChartData({
          labels: timeData.labels,
          datasets: [
            {
              label: 'Average Response Time (ms)',
              data: timeData.avgResponseTimes,
              borderColor: theme.palette.primary.main,
              backgroundColor: `${theme.palette.primary.main}20`,
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointRadius: isMobile ? 2 : 4,
              pointHoverRadius: isMobile ? 4 : 6,
            },
            {
              label: 'Max Response Time (ms)',
              data: timeData.maxResponseTimes,
              borderColor: theme.palette.error.main,
              backgroundColor: `${theme.palette.error.main}10`,
              borderWidth: 1,
              fill: false,
              tension: 0.4,
              pointRadius: isMobile ? 1 : 2,
              pointHoverRadius: isMobile ? 3 : 4,
              borderDash: [5, 5],
            }
          ],
        });
      } catch (err) {
        console.error("Error fetching response time data:", err);
        setError(err.message || "Failed to fetch response time data.");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [theme.palette.primary.main, theme.palette.error.main, isMobile]);

  const processResponseTimeData = (logs) => {
    // Group logs by hour for the last 24 hours
    const hours = 24;
    const now = moment();
    const labels = [];
    const avgResponseTimes = [];
    const maxResponseTimes = [];

    for (let i = hours - 1; i >= 0; i--) {
      const hourStart = moment(now).subtract(i, 'hours').startOf('hour');
      const hourEnd = moment(hourStart).endOf('hour');
      
      labels.push(hourStart.format('HH:mm'));
      
      const hourLogs = logs.filter(log => {
        const logTime = moment(log.executedAt);
        return logTime.isBetween(hourStart, hourEnd, null, '[]');
      });

      if (hourLogs.length > 0) {
        const responseTimes = hourLogs
          .filter(log => log.responseTime)
          .map(log => log.responseTime);
        
        if (responseTimes.length > 0) {
          const avg = responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length;
          const max = Math.max(...responseTimes);
          avgResponseTimes.push(Math.round(avg));
          maxResponseTimes.push(max);
        } else {
          avgResponseTimes.push(0);
          maxResponseTimes.push(0);
        }
      } else {
        avgResponseTimes.push(0);
        maxResponseTimes.push(0);
      }
    }

    return { labels, avgResponseTimes, maxResponseTimes };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'top',
        labels: {
          font: { size: isMobile ? 10 : 12 },
          usePointStyle: true,
          padding: isMobile ? 10 : 20,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        titleFont: { size: isMobile ? 10 : 12 },
        bodyFont: { size: isMobile ? 10 : 12 },
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y}ms`;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: !isMobile,
          text: 'Time (Last 24 Hours)',
          font: { size: isMobile ? 10 : 12 },
        },
        ticks: {
          font: { size: isMobile ? 8 : 10 },
          maxRotation: isMobile ? 45 : 0,
        },
        grid: {
          display: false,
        }
      },
      y: {
        display: true,
        title: {
          display: !isMobile,
          text: 'Response Time (ms)',
          font: { size: isMobile ? 10 : 12 },
        },
        ticks: {
          font: { size: isMobile ? 8 : 10 },
          callback: function(value) {
            return value + 'ms';
          }
        },
        grid: {
          color: theme.palette.divider,
        }
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
    elements: {
      point: {
        hoverBackgroundColor: theme.palette.primary.main,
      }
    }
  };

  if (loading) {
    return (
      <Card sx={{ 
        height: 400,
        borderRadius: '6px',
        boxShadow: theme.shadows[2],
        '&:hover': { boxShadow: theme.shadows[4] }
      }}>
        <CardContent sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: isMobile ? 2 : 3
        }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading response time data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ 
        height: 400,
        boxShadow: theme.shadows[2],
        '&:hover': { boxShadow: theme.shadows[4] }
      }}>
        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!chartData) {
    return (
      <Card sx={{ 
        height: 400,
        boxShadow: theme.shadows[2],
        '&:hover': { boxShadow: theme.shadows[4] }
      }}>
        <CardContent sx={{ 
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: isMobile ? 2 : 3
        }}>
          <Typography variant="body1" color="text.secondary">
            No response time data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      height: 400,
      boxShadow: theme.shadows[2],
      '&:hover': { boxShadow: theme.shadows[4] }
    }}>
      <CardContent sx={{ 
        height: '100%', 
        p: isMobile ? 2 : 3,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          sx={{ 
            fontWeight: 600,
            mb: 2,
            textAlign: 'center'
          }}
        >
          API Response Times
        </Typography>
        
        <Box sx={{ 
          flexGrow: 1,
          height: 'calc(100% - 60px)',
          position: 'relative'
        }}>
          <Line data={chartData} options={chartOptions} />
        </Box>
      </CardContent>
    </Card>
  );
};

export default ResponseTimeChart; 